import { useTranslation } from 'react-i18next'
import { mediaUrl } from '../lib/sightingsApi'
import type { LostListItem } from '../lib/lostApi'
import { formatRelativePast } from '../lib/reportHeadline'

interface Props {
  open: boolean
  items: LostListItem[]
  selectedAnimalId: number | null
  onSelect: (animalId: number) => void
  onClose: () => void
}

export function LostPanel({ open, items, selectedAnimalId, onSelect, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage || i18n.language || 'en'

  return (
    <aside
      id="active-lost-panel"
      className={`side-panel${open ? ' open' : ''}`}
      aria-label={t('panel.title')}
      aria-hidden={!open}
    >
      <div className="panel-header panel-header-row">
        <h2 className="panel-title">{t('panel.lostTitle')}</h2>
        <button type="button" className="btn btn-ghost panel-close" onClick={onClose}>
          {t('panel.closePanel')}
        </button>
      </div>
      <div className="panel-body">
        {items.length === 0 ? (
          <p className="empty-state">{t('panel.emptyLost')}</p>
        ) : (
          items.map((item) => {
            const animal = item.animal
            const lastSeenAgo = formatRelativePast(item.missing_noticed_at, locale)
            return (
              <article
                key={item.id}
                className={`lost-card${selectedAnimalId === animal.id ? ' active' : ''}${item.thumbnail_key ? ' has-photo' : ''}`}
                onClick={() => onSelect(animal.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onSelect(animal.id)
                }}
                role="button"
                tabIndex={0}
              >
                <div className="lost-card-copy">
                  <div className="lost-id">
                    #{String(animal.id).padStart(4, '0')} · {t(`types.${animal.type}`)}
                    {lastSeenAgo ? ` · ${lastSeenAgo}` : ''}
                  </div>
                  <div className="lost-meta">
                    {t(`colors.${animal.color1}`)}
                    {animal.color2 ? ` / ${t(`colors.${animal.color2}`)}` : ''}
                    <small>
                      {animal.chip
                        ? `${t('fields.chip')}: ${animal.chip}`
                        : t('fields.unknown')}
                      {' · '}
                      {item.contact_info}
                    </small>
                  </div>
                </div>
                {item.thumbnail_key && (
                  <div className="lost-card-photo">
                    <img
                      src={mediaUrl(item.thumbnail_key)}
                      alt={t('profile.lostPhoto', { id: animal.id })}
                    />
                  </div>
                )}
              </article>
            )
          })
        )}
      </div>
    </aside>
  )
}
